import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import "../styles/Stats.css";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF", "#FF4560"];

const calculateDaysWorked = (tarihMap) => {
  const today = new Date();
  let totalDays = 0;
  for (let i = 1; i <= 9; i++) {
    const giris = tarihMap[`giris${i}`];
    const cikis = tarihMap[`cikis${i}`];
    if (giris) {
      const [g, a, y] = giris.split("-").map(Number);
      const start = new Date(y, a - 1, g);
      const end = cikis ? new Date(...cikis.split("-").reverse().map(Number)) : today;
      const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      if (days > 0) totalDays += days;
    }
  }
  return totalDays;
};

const Istatistikler = () => {
  const [kazalar, setKazalar] = useState([]);
  const [personeller, setPersoneller] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const kazaSnap = await getDocs(collection(db, "kazalar"));
      const kazaData = kazaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const personelSnap = await getDocs(collection(db, "personeller"));
      const personelData = personelSnap.docs.map(doc => doc.data());
      setKazalar(kazaData);
      setPersoneller(personelData);
    };
    fetchData();
  }, []);

  const getTesisAd = (adSoyad) => {
    const personel = personeller.find(p => `${p.ad} ${p.soyad}` === adSoyad);
    return personel?.tesis || "Belirtilmemiş";
  };

  const getTesisDistribution = () => {
    const counts = {};
    kazalar.forEach(k => {
      const tesis = getTesisAd(k.personel);
      counts[tesis] = (counts[tesis] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  };

  const getYilAylikDistribution = () => {
    const aylar = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    const dist = {};
    kazalar.forEach(k => {
      if (k.tarih?.seconds) {
        const date = new Date(k.tarih.seconds * 1000);
        const yil = date.getFullYear();
        const ay = date.getMonth();
        if (!dist[yil]) dist[yil] = Array(12).fill(0);
        dist[yil][ay]++;
      }
    });
    const result = aylar.map((ay, idx) => {
      const data = { ay };
      for (const yil in dist) data[yil] = dist[yil][idx] || 0;
      return data;
    });
    return { data: result, years: Object.keys(dist) };
  };

  const getTopKazaciList = () => {
    const counts = {};
    kazalar.forEach(k => {
      if (!counts[k.personel]) counts[k.personel] = 0;
      counts[k.personel]++;
    });
    return Object.entries(counts)
      .map(([adSoyad, kazaSayisi]) => {
        const p = personeller.find(p => `${p.ad} ${p.soyad}` === adSoyad);
        const gun = p?.tarih ? calculateDaysWorked(p.tarih) : 0;
        return {
          adSoyad,
          kazaSayisi,
          gun,
          oran: gun > 0 ? (gun / kazaSayisi).toFixed(1) : "-",
          tesis: p?.tesis,
          gorev: p?.gorev
        };
      })
      .filter(p => {
        const tc = personeller.find(pe => `${pe.ad} ${pe.soyad}` === p.adSoyad)?.tc;
        const kayit = personeller.find(pe => pe.tc === tc);
        const tarih = kayit?.tarih || {};
        return tarih.giris1 && !tarih.cikis1;
      })
      .sort((a, b) => b.kazaSayisi - a.kazaSayisi)
      .slice(0, 10);
  };

  const { data: yilAyData, years } = getYilAylikDistribution();

  return (
    <div className="istatistik-container">
      <h2>📈 İstatistikler</h2>

      <div className="chart-section">
        <h3>Tesis Bazlı Dağılım</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getTesisDistribution()} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" name="Kaza Sayısı" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-section">
        <h3>Yıllara ve Aylara Göre Kaza Dağılımı</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={yilAyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="ay" />
            <YAxis />
            <Tooltip />
            <Legend />
            {years.map((year, index) => (
              <Bar key={year} dataKey={year} fill={COLORS[index % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-section" style={{ maxWidth: 600, textAlign: "left" }}>
        <h3>En Çok Kaza Yapan Personeller (İlk 10)</h3>
        <table className="top-table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>Kaza Sayısı</th>
              <th>Gün/Kaza</th>
            </tr>
          </thead>
          <tbody>
            {getTopKazaciList().map((p, i) => (
              <tr key={i} title={`Tesis: ${p.tesis}\nGörev: ${p.gorev}\nToplam Gün: ${p.gun}`}>
                <td>{p.adSoyad}</td>
                <td>{p.kazaSayisi}</td>
                <td>{p.oran}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Istatistikler;
